/**
 * Two phones side-by-side: Vorher vs. Nachher with a VS pill in between.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { phoneFrame, screenContent } from "./_mockHelpers.js";

export const phoneSplitCompare: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const eyebrow = inputs.subtext ?? "";
  const labels = (inputs.items?.length ? inputs.items : ["Vorher", "Nachher"]).slice(0, 2);
  const isVertical = ctx.format === "9:16";
  const phoneW = isVertical ? 320 : 360;

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const eyeId = `${clipPrefix}-eye`;
  const leftId = `${clipPrefix}-left`;
  const rightId = `${clipPrefix}-right`;
  const vsId = `${clipPrefix}-vs`;
  const labelLId = `${clipPrefix}-lbl-l`;
  const labelRId = `${clipPrefix}-lbl-r`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const leftInner = `<div style="position:absolute;inset:0;filter:grayscale(1) brightness(0.7);">${screenContent({ media: inputs.media, mediaIndex: 0, accent: themeColors.muted, fallback: "list" })}</div>`;
  const rightInner = screenContent({ media: inputs.media, mediaIndex: 1, accent, fallback: "feed" });

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:32px;padding:64px;">
    ${eyebrow ? `<div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.78}px;letter-spacing:6px;text-transform:uppercase;color:${accent};">${escapeHtml(eyebrow)}</div>` : ""}
    ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.78}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1.02;text-align:center;text-wrap:balance;max-width:${isVertical ? 820 : 1100}px;">${escapeHtml(headline)}</div>` : ""}
    <div style="position:relative;display:flex;align-items:center;justify-content:center;gap:40px;">
      <div style="display:flex;flex-direction:column;align-items:center;gap:18px;">
        ${phoneFrame({ id: leftId, width: phoneW, accent: withAlpha(themeColors.fg, 0.3), inner: leftInner })}
        <div id="${labelLId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${md * 0.55}px;letter-spacing:6px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.65)};padding:8px 18px;border-radius:9999px;background:${withAlpha(themeColors.fg, 0.08)};border:1px solid ${withAlpha(themeColors.fg, 0.18)};">${escapeHtml(labels[0] ?? "Vorher")}</div>
      </div>
      <div id="${vsId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${md * 0.85}px;letter-spacing:2px;color:${themeColors.fg};padding:14px 22px;border-radius:9999px;background:linear-gradient(135deg, ${accent} 0%, ${withAlpha(accent, 0.7)} 100%);box-shadow:0 0 30px ${withAlpha(accent, 0.6)};color:${themeColors.bg};">VS</div>
      <div style="display:flex;flex-direction:column;align-items:center;gap:18px;">
        ${phoneFrame({ id: rightId, width: phoneW, accent, inner: rightInner })}
        <div id="${labelRId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${md * 0.55}px;letter-spacing:6px;text-transform:uppercase;color:${themeColors.bg};padding:8px 18px;border-radius:9999px;background:${accent};box-shadow:0 0 24px ${withAlpha(accent, 0.55)};">${escapeHtml(labels[1] ?? "Nachher")}</div>
      </div>
    </div>
  </div>`;

  const init = [
    eyebrow ? gsapSetLine(`#${eyeId}`, { opacity: 0, y: 10 }) : "",
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 14 }) : "",
    gsapSetLine(`#${leftId}`, { opacity: 0, x: -60, rotation: -6 }),
    gsapSetLine(`#${rightId}`, { opacity: 0, x: 60, rotation: 6 }),
    gsapSetLine(`#${vsId}`, { opacity: 0, scale: 0.5 }),
    gsapSetLine(`#${labelLId}`, { opacity: 0, y: 10 }),
    gsapSetLine(`#${labelRId}`, { opacity: 0, y: 10 }),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${leftId}`, { opacity: 1, x: 0, rotation: 0, duration: 0.85, ease: "raw:\"power2.out\"" }, 0.1),
    t.to(`#${rightId}`, { opacity: 1, x: 0, rotation: 0, duration: 0.85, ease: "raw:\"power2.out\"" }, 0.15),
    t.to(`#${vsId}`, { opacity: 1, scale: 1, duration: 0.7, ease: "raw:\"back.out(2)\"" }, 0.5),
    t.to(`#${vsId}`, { rotation: 360, duration: clipDurationSec, ease: "raw:\"none\"" }, 0),
    t.to(`#${labelLId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.7),
    t.to(`#${labelRId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.85),
  ];
  if (eyebrow) tweens.push(t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.05));
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.25));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
